import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { ProjectProvider, useProject } from '../../context/ProjectContext';
import { PackageGatesProvider, usePackageGates } from '../../context/PackageGatesContext';
import { 
  LayoutDashboard, 
  Search, 
  FileText, 
  CheckSquare, 
  ListTodo,
  BarChart3,
  CreditCard,
  Settings,
  LogOut,
  Bell,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Menu,
  X,
  Users,
  Target,
  CheckCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  Shield,
  MapPin,
  Globe,
  History,
  Link2,
  Image,
  Send,
  Share2,
  Video,
  Code2,
  Plug,
  Rss,
  FileCode,
  ShieldAlert,
  Map,
  Camera,
  PieChart,
  Pin,
  TrendingUp,
  Bookmark,
  Copy,
  BarChart2,
  Package,
  Zap,
  AlertTriangle, // kept for potential future use
  Mail,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { useAnyTaskRunning } from '../../context/TaskStateContext';

// Maps nav path → backend feature key for package-tier gating.
// Only client nav items with a gated feature need an entry here.
const NAV_FEATURE = {
  '/dashboard/gbp':              'google_business_page',
  '/dashboard/competitor':       'competitor_analysis',
  '/dashboard/backlinks':        'backlink_analysis',
  '/dashboard/images':           'infographics',
  '/dashboard/bing-webmaster':   'bing_webmaster',
  '/dashboard/structured-data':  'structured_data',
  '/dashboard/press-releases':   'press_releases',
  '/dashboard/geotagging':       'geotagging',
};

const RUNNING_TASK_KEYS = {
  '/dashboard/onpage': ['onpage-scan'],
  '/dashboard/keywords': ['keywords-ai-suggestions'],
  '/dashboard/gbp': ['gbp-ai-generate'],
  '/dashboard/competitor': [
    'competitor-content-gap',
    'competitor-site-comparison',
    'competitor-keyword-gap',
    'competitor-web-intel',
  ],
  '/dashboard/content': ['content-generate', 'content-humanize'],
  '/dashboard/backlinks': ['backlinks-outreach'],
  '/dashboard/images': ['images-generate'],
  '/dashboard/content-distribution': ['social-generate'],
  '/dashboard/offpage-seo': ['offpage-discover'],
};

const NAV_ITEMS = {
  client: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
    { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
    { icon: Target, label: 'Keywords', path: '/dashboard/keywords' },
    { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
    { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
    { icon: Image, label: 'Images', path: '/dashboard/images' },
    { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
    { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
    { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
    { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
    { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
    { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
    { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
    { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
    { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
    { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
    { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
    { icon: FileText, label: 'Content', path: '/dashboard/content' },
    { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
    { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
    { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
    { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
    { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
    { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
    { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
    { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
    { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
    { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
    { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
    { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
    { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
    { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
    { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
    { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
    { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: BarChart3, label: 'Reports', path: '/dashboard/reports' },
    { icon: CreditCard, label: 'Billing', path: '/dashboard/billing' },
    { icon: BarChart2, label: 'Package Usage', path: '/dashboard/package-usage' },
    { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  project_manager: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Clients', path: '/dashboard/execution' },
    { icon: ListTodo, label: 'Tasks', path: '/dashboard/tasks' },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
    { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
    { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
    { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
    { icon: Image, label: 'Images', path: '/dashboard/images' },
    { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
    { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
    { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
    { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
    { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
    { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
    { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
    { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
    { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
    { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
    { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
    { icon: History, label: 'Execution Log', path: '/dashboard/execution-log' },
    { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
    { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
    { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
    { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
    { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
    { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
    { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
    { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
    { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
    { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
    { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
    { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
    { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
    { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
    { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
    { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
    { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: FileText, label: 'Content', path: '/dashboard/content' },
    { icon: BarChart3, label: 'Reports', path: '/dashboard/reports' },
    { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Shield, label: 'User Management', path: '/dashboard/admin/users' },
    { icon: Package, label: 'Package Limits', path: '/dashboard/admin/packages' },
    { icon: Zap, label: 'AI Usage & Cost', path: '/dashboard/admin/llm-usage' },
    { icon: Users, label: 'Clients', path: '/dashboard/execution' },
    { icon: Target, label: 'Projects', path: '/dashboard/projects' },
    { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
    { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
    { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
    { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
    { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
    { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
    { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
    { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
    { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
    { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
    { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
    { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
    { icon: Image, label: 'Images', path: '/dashboard/images' },
    { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
    { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
    { icon: ListTodo, label: 'Tasks', path: '/dashboard/tasks' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals' },
    { icon: History, label: 'Execution Log', path: '/dashboard/execution-log' },
    { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
    { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
    { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
    { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
    { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
    { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
    { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
    { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
    { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
    { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
    { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
    { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
    { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
    { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
    { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
    { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
    { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: BarChart2, label: 'Package Usage', path: '/dashboard/package-usage' },
    { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  agency: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/agency' },
    { icon: Users, label: 'My Clients', path: '/dashboard/agency' },
    { icon: CreditCard, label: 'Billing', path: '/dashboard/billing' },
    { icon: BarChart2, label: 'Package Usage', path: '/dashboard/package-usage' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  execution_team: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/execution' },
    { icon: ListTodo, label: 'My Tasks', path: '/dashboard/tasks' },
    { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
    { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: History, label: 'Execution Log', path: '/dashboard/execution-log' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  content_account: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: ListTodo, label: 'My Tasks', path: '/dashboard/tasks' },
    { icon: FileText, label: 'Content', path: '/dashboard/content' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
  creative_account: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: ListTodo, label: 'My Tasks', path: '/dashboard/tasks' },
    { icon: FileText, label: 'Assets', path: '/dashboard/content' },
    { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
    { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ],
};

// Sectioned nav for roles with many items. Each section has { id, label, icon, items }.
// Roles not listed here fall back to the flat NAV_ITEMS arrays.
const NAV_SECTIONS = {
  client: [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: BarChart3, label: 'Reports', path: '/dashboard/reports' },
        { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
        { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
        { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
      ],
    },
    {
      id: 'rankings',
      label: 'Rankings & Keywords',
      icon: TrendingUp,
      items: [
        { icon: Target, label: 'Keywords', path: '/dashboard/keywords' },
        { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
        { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
      ],
    },
    {
      id: 'technical',
      label: 'Technical SEO',
      icon: ShieldAlert,
      items: [
        { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
        { icon: Zap, label: 'Core Web Vitals', path: '/dashboard/core-web-vitals' },
        { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
        { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
        { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
        { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
        { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
        { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
        { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
      ],
    },
    {
      id: 'offpage',
      label: 'Off-Page & Links',
      icon: Link2,
      items: [
        { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
        { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
        { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
        { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
      ],
    },
    {
      id: 'local',
      label: 'Local & Business',
      icon: MapPin,
      items: [
        { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
        { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
        { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
        { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
        { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
      ],
    },
    {
      id: 'content',
      label: 'Content & Distribution',
      icon: FileText,
      items: [
        { icon: FileText, label: 'Content', path: '/dashboard/content' },
        { icon: Image, label: 'Images', path: '/dashboard/images' },
        { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
        { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
        { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
        { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
        { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
        { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
        { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
        { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
        { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
        { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
        { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
      ],
    },
    {
      id: 'account',
      label: 'Account',
      icon: Settings,
      items: [
        { icon: CreditCard, label: 'Billing', path: '/dashboard/billing' },
        { icon: BarChart2, label: 'Package Usage', path: '/dashboard/package-usage' },
        { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
        { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
        { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
        { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
      ],
    },
  ],
  project_manager: [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Users, label: 'Clients', path: '/dashboard/execution' },
        { icon: ListTodo, label: 'Tasks', path: '/dashboard/tasks' },
        { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
        { icon: BarChart3, label: 'Reports', path: '/dashboard/reports' },
        { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals', badge: true },
        { icon: History, label: 'Execution Log', path: '/dashboard/execution-log' },
        { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
      ],
    },
    {
      id: 'rankings',
      label: 'Rankings & Keywords',
      icon: TrendingUp,
      items: [
        { icon: Target, label: 'Keywords', path: '/dashboard/keywords' },
        { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
        { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
      ],
    },
    {
      id: 'technical',
      label: 'Technical SEO',
      icon: ShieldAlert,
      items: [
        { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
        { icon: Zap, label: 'Core Web Vitals', path: '/dashboard/core-web-vitals' },
        { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
        { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
        { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
        { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
        { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
        { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
        { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
      ],
    },
    {
      id: 'offpage',
      label: 'Off-Page & Links',
      icon: Link2,
      items: [
        { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
        { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
        { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
        { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
      ],
    },
    {
      id: 'local',
      label: 'Local & Business',
      icon: MapPin,
      items: [
        { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
        { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
        { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
        { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
        { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
      ],
    },
    {
      id: 'content',
      label: 'Content & Distribution',
      icon: FileText,
      items: [
        { icon: FileText, label: 'Content', path: '/dashboard/content' },
        { icon: Image, label: 'Images', path: '/dashboard/images' },
        { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
        { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
        { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
        { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
        { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
        { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
        { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
        { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
        { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
        { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
        { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
      ],
    },
    {
      id: 'account',
      label: 'Account',
      icon: Settings,
      items: [
        { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
        { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
        { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
        { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
      ],
    },
  ],
  admin: [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: Shield, label: 'User Management', path: '/dashboard/admin/users' },
        { icon: Package, label: 'Package Limits', path: '/dashboard/admin/packages' },
        { icon: Zap, label: 'AI Usage & Cost', path: '/dashboard/admin/llm-usage' },
        { icon: Users, label: 'Clients', path: '/dashboard/execution' },
        { icon: Target, label: 'Projects', path: '/dashboard/projects' },
        { icon: ListTodo, label: 'Tasks', path: '/dashboard/tasks' },
        { icon: Calendar, label: 'Calendar', path: '/dashboard/calendar' },
        { icon: CheckSquare, label: 'Approvals', path: '/dashboard/approvals' },
        { icon: History, label: 'Execution Log', path: '/dashboard/execution-log' },
        { icon: Bell, label: 'Notifications', path: '/dashboard/notifications' },
        { icon: BarChart2, label: 'Package Usage', path: '/dashboard/package-usage' },
      ],
    },
    {
      id: 'rankings',
      label: 'Rankings & Keywords',
      icon: TrendingUp,
      items: [
        { icon: Search, label: 'Keywords', path: '/dashboard/keywords' },
        { icon: TrendingUp, label: 'SERP Ranking', path: '/dashboard/serp-ranking' },
        { icon: BarChart3, label: 'Competitor Analysis', path: '/dashboard/competitor' },
      ],
    },
    {
      id: 'technical',
      label: 'Technical SEO',
      icon: ShieldAlert,
      items: [
        { icon: Search, label: 'On-Page SEO', path: '/dashboard/onpage' },
        { icon: Zap, label: 'Core Web Vitals', path: '/dashboard/core-web-vitals' },
        { icon: ShieldAlert, label: 'Site Audit', path: '/dashboard/site-audit' },
        { icon: Map, label: 'Sitemap', path: '/dashboard/sitemap' },
        { icon: FileCode, label: 'Robots.txt', path: '/dashboard/robots-txt' },
        { icon: Code2, label: 'Structured Data', path: '/dashboard/structured-data' },
        { icon: Search, label: 'Indexed Pages Check', path: '/dashboard/indexation' },
        { icon: ShieldAlert, label: 'Penalty Check', path: '/dashboard/penalty-check' },
        { icon: Copy, label: 'Duplicate Content', path: '/dashboard/duplicate-content' },
      ],
    },
    {
      id: 'offpage',
      label: 'Off-Page & Links',
      icon: Link2,
      items: [
        { icon: Globe, label: 'Off-Page SEO', path: '/dashboard/offpage-seo' },
        { icon: Link2, label: 'Backlinks', path: '/dashboard/backlinks' },
        { icon: FileText, label: 'Press Releases', path: '/dashboard/press-releases' },
        { icon: Link2, label: 'Smart Links', path: '/dashboard/smart-links' },
      ],
    },
    {
      id: 'local',
      label: 'Local & Business',
      icon: MapPin,
      items: [
        { icon: MapPin, label: 'Local SEO', path: '/dashboard/local-seo' },
        { icon: MapPin, label: 'Google Business', path: '/dashboard/gbp' },
        { icon: MapPin, label: 'Bing Places', path: '/dashboard/bing-places' },
        { icon: Share2, label: 'NAP Syndication', path: '/dashboard/syndication' },
        { icon: MapPin, label: 'Geotagging', path: '/dashboard/geotagging' },
        { icon: Globe, label: 'Website CMS', path: '/dashboard/cms' },
      ],
    },
    {
      id: 'content',
      label: 'Content & Distribution',
      icon: FileText,
      items: [
        { icon: Image, label: 'Images', path: '/dashboard/images' },
        { icon: Video, label: 'Video SEO', path: '/dashboard/video-seo' },
        { icon: Send, label: 'Distribution', path: '/dashboard/content-distribution' },
        { icon: Globe, label: 'Web 2.0 Profiles', path: '/dashboard/web20-profiles' },
        { icon: Rss, label: 'Micro Blogging', path: '/dashboard/microblog' },
        { icon: Bookmark, label: 'Social Bookmarking', path: '/dashboard/social-bookmarking' },
        { icon: Pin, label: 'Pinterest', path: '/dashboard/pinterest' },
        { icon: Camera, label: 'Image Sharing', path: '/dashboard/image-sharing' },
        { icon: PieChart, label: 'Infographic Distribution', path: '/dashboard/infographic-distribution' },
        { icon: FileText, label: 'Classified Submissions', path: '/dashboard/classified-submissions' },
        { icon: FileText, label: 'PPT Submissions', path: '/dashboard/ppt-submissions' },
        { icon: Share2, label: 'Social Media', path: '/dashboard/social' },
        { icon: BarChart3, label: 'Bing Webmaster', path: '/dashboard/bing-webmaster' },
      ],
    },
    {
      id: 'account',
      label: 'System',
      icon: Settings,
      items: [
        { icon: Plug, label: 'Integrations', path: '/dashboard/integrations' },
        { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
      ],
    },
  ],
};

const NOTIFICATION_ICONS = {
  approval_requested: { icon: Clock, color: 'bg-amber-100 text-amber-600' },
  approval_granted: { icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-600' },
  approval_rejected: { icon: XCircle, color: 'bg-red-100 text-red-600' },
  task_assigned: { icon: FileText, color: 'bg-indigo-100 text-indigo-600' },
  system: { icon: Bell, color: 'bg-slate-100 text-slate-600' },
};

function NavRunningDot({ taskKeys }) {
  const running = useAnyTaskRunning(taskKeys);
  if (!running) return null;
  return (
    <span className="ml-auto relative flex h-2 w-2 shrink-0">
      <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-indigo-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
    </span>
  );
}

const PACKAGE_COLORS = {
  basic: 'text-slate-500',
  silver: 'text-slate-400',
  gold: 'text-yellow-600',
  platinum: 'text-blue-500',
};

const PRIVILEGED_ROLES = new Set([
  'admin',
  'project_manager',
  'content_account',
  'creative_account',
  'execution_team',
]);

function ProjectSelector({ user }) {
  const { projects, selectedProjectId, setSelectedProjectId } = useProject();
  const isPrivilegedUser = PRIVILEGED_ROLES.has(user?.role);
  if (!projects.length) return null;
  const selected = projects.find(p => String(p.project_id) === selectedProjectId);
  const pkg = selected?.package_type;
  return (
    <div className="hidden sm:flex items-center gap-2">
      <select
        value={selectedProjectId}
        onChange={e => setSelectedProjectId(e.target.value)}
        className="h-8 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-slate-700 dark:text-slate-200 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[180px]"
      >
        {projects.map(p => (
          <option key={p.project_id} value={String(p.project_id)}>
            {p.name}
          </option>
        ))}
      </select>
      {pkg && !isPrivilegedUser && (
        <span className={`text-xs font-semibold capitalize ${PACKAGE_COLORS[pkg] || ''}`}>
          {pkg}
        </span>
      )}
    </div>
  );
}

function DashboardLayoutInner() {
  const { user, logout } = useAuth();
  const isPrivilegedUser = PRIVILEGED_ROLES.has(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectedProject } = useProject();
  const { canAccess, hasPackage } = usePackageGates();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [navSearch, setNavSearch] = useState('');

  const storageKey = `gds:nav:collapsed:${user?.role || 'client'}`;
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });
  const toggleSection = (id) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Fetch notifications on mount and periodically
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [notifResponse, countResponse] = await Promise.all([
          api.get('/notifications', { params: { page: 1, page_size: 10 } }),
          api.get('/notifications/unread-count')
        ]);
        setNotifications(notifResponse.data?.items || notifResponse.data || []);
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    const fetchPendingApprovals = async () => {
      try {
        const res = await api.get('/approvals/pending');
        setPendingApprovals(res.data?.total ?? (Array.isArray(res.data?.items) ? res.data.items.length : (Array.isArray(res.data) ? res.data.length : 0)));
      } catch { /* ignore */ }
    };

    fetchNotifications();
    fetchPendingApprovals();
    const interval = setInterval(fetchNotifications, 30000);
    const approvalsInterval = setInterval(fetchPendingApprovals, 60000);
    return () => { clearInterval(interval); clearInterval(approvalsInterval); };
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  const allNavItems = NAV_ITEMS[user?.role] || NAV_ITEMS.client;
  const navItems = user?.role === 'client'
    ? allNavItems.filter(item => {
        const feature = NAV_FEATURE[item.path];
        if (feature && !canAccess(feature, selectedProject?.package_type)) return false;
        if (!hasPackage && item.path === '/dashboard/package-usage') return false;
        return true;
      })
    : allNavItems;
  const normalizedNavSearch = navSearch.trim().toLowerCase();
  const filteredNavItems = normalizedNavSearch
    ? navItems.filter(item => (
        item.label.toLowerCase().includes(normalizedNavSearch)
        || item.path.toLowerCase().includes(normalizedNavSearch)
      ))
    : navItems;

  // Sectioned nav: apply package gating per item, hide empty sections
  const rawSections = NAV_SECTIONS[user?.role];
  const navSections = rawSections
    ? rawSections.map(section => ({
        ...section,
        items: section.items.filter(item => {
          const feature = NAV_FEATURE[item.path];
          if (feature && !canAccess(feature, selectedProject?.package_type)) return false;
          if (!isPrivilegedUser && !hasPackage && item.path === '/dashboard/package-usage') return false;
          return true;
        }),
      })).filter(section => section.items.length > 0)
    : null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleBadge = (role) => {
    const roles = {
      client: { label: 'Client', color: 'bg-blue-100 text-blue-700' },
      project_manager: { label: 'Project Manager', color: 'bg-purple-100 text-purple-700' },
      admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
      content_account: { label: 'Content Team', color: 'bg-amber-100 text-amber-700' },
      creative_account: { label: 'Creative Team', color: 'bg-pink-100 text-pink-700' },
      execution_team: { label: 'Execution Team', color: 'bg-cyan-100 text-cyan-700' },
    };
    return roles[role] || roles.client;
  };

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 
        transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 transition-transform duration-300 ease-in-out
        bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        flex flex-col
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-heading font-bold text-lg">GDS</span>
              <p className="text-xs text-slate-500">Go Digital Step</p>
            </div>
          </Link>
        </div>

        {/* Feature search */}
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Search features..."
              aria-label="Search dashboard features"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-9 text-sm text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200 dark:focus:border-indigo-500 dark:focus:bg-slate-800 dark:focus:ring-indigo-900/40"
            />
            {navSearch && (
              <button
                type="button"
                onClick={() => setNavSearch('')}
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                aria-label="Clear feature search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {navSections && !normalizedNavSearch ? (
            // Sectioned navigation
            <div className="space-y-1">
              {navSections.map((section) => {
                const isCollapsed = collapsedSections[section.id];
                const SectionIcon = section.icon;
                return (
                  <div key={section.id}>
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors select-none"
                    >
                      <SectionIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="flex-1 text-left">{section.label}</span>
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
                    </button>
                    {!isCollapsed && (
                      <div className="mt-0.5 mb-2 space-y-0.5">
                        {section.items.map((item) => {
                          const isActive = location.pathname === item.path;
                          const taskKeys = RUNNING_TASK_KEYS[item.path];
                          return (
                            <Link
                              key={item.label}
                              to={item.path}
                              onClick={() => setSidebarOpen(false)}
                              className={isActive ? 'gds-nav-item-active' : 'gds-nav-item'}
                              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                              <item.icon className="w-5 h-5" />
                              <span className="flex-1">{item.label}</span>
                              {item.badge && pendingApprovals > 0 && (
                                <Badge variant="destructive" className="ml-auto">
                                  {pendingApprovals}
                                </Badge>
                              )}
                              {taskKeys && <NavRunningDot taskKeys={taskKeys} />}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Flat navigation (non-section roles or search active)
            <div className="space-y-1">
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const taskKeys = RUNNING_TASK_KEYS[item.path];
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={isActive ? 'gds-nav-item-active' : 'gds-nav-item'}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge && pendingApprovals > 0 && (
                      <Badge variant="destructive" className="ml-auto">
                        {pendingApprovals}
                      </Badge>
                    )}
                    {taskKeys && <NavRunningDot taskKeys={taskKeys} />}
                  </Link>
                );
              })}
              {filteredNavItems.length === 0 && (
                <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No features found
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Activate plan CTA — only shown to packageless client users */}
        {user?.role === 'client' && !hasPackage && (
          <div className="px-4 pb-3">
            <Link
              to="/dashboard/billing"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-2 w-full rounded-xl px-3 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
              data-testid="nav-activate-plan"
            >
              <Package className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Activate Plan</span>
            </Link>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className={`text-xs px-2 py-0.5 rounded-full inline-block ${getRoleBadge(user?.role).color}`}>
                {getRoleBadge(user?.role).label}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h1 className="font-heading font-semibold text-lg hidden sm:block">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <ProjectSelector user={user} />
            {/* Notifications Popover */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-7"
                      onClick={handleMarkAllAsRead}
                    >
                      <CheckCheck className="w-3 h-3 mr-1" />
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[200px] text-slate-500">
                      <Bell className="w-8 h-8 mb-2 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {notifications.slice(0, 5).map((notification) => {
                        const typeConfig = NOTIFICATION_ICONS[notification.notification_type] || NOTIFICATION_ICONS.system;
                        const TypeIcon = typeConfig.icon;
                        
                        return (
                          <div
                            key={notification.notification_id}
                            className={`p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors ${
                              !notification.is_read ? 'bg-indigo-50/50 dark:bg-indigo-900/20' : ''
                            }`}
                            onClick={() => {
                              if (!notification.is_read) {
                                handleMarkAsRead(notification.notification_id);
                              }
                              setNotificationsOpen(false);
                              if (notification.link) {
                                navigate(notification.link);
                              }
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              if (!notification.is_read) {
                                handleMarkAsRead(notification.notification_id);
                              }
                              setNotificationsOpen(false);
                              if (notification.link) {
                                navigate(notification.link);
                              }
                            }}
                          >
                            <div className="flex gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                                <TypeIcon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${!notification.is_read ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                                  {notification.title}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {formatTime(notification.created_at)}
                                </p>
                              </div>
                              {!notification.is_read && (
                                <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                <div className="p-2 border-t border-slate-200 dark:border-slate-700">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm"
                    onClick={() => {
                      navigate('/dashboard/notifications');
                      setNotificationsOpen(false);
                    }}
                  >
                    View all notifications
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                {!isPrivilegedUser && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard/billing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Billing
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Pages always accessible regardless of plan */}
          {(() => {
            const OPEN_PATHS = ['/dashboard', '/dashboard/billing', '/dashboard/settings', '/dashboard/integrations'];
            const isOpenPage = OPEN_PATHS.includes(location.pathname);
            const showGate = !isPrivilegedUser && !hasPackage && !isOpenPage;

            return showGate ? (
              /* Blur gate: render the page behind a blurred overlay so the user can
                 glimpse the content but cannot interact with it */
              <div className="relative min-h-[calc(100vh-4rem-3rem)]">
                {/* Blurred page content — pointer-events disabled so nothing is clickable */}
                <div className="select-none pointer-events-none blur-sm brightness-75 saturate-50">
                  <Outlet />
                </div>

                {/* Centered upgrade card overlay — fixed to main viewport so it stays centered regardless of content height */}
                <div className="fixed inset-0 flex items-center justify-center z-10 px-4 pointer-events-none pt-16 lg:pl-64">
                  <div className="pointer-events-auto">
                  <div
                    className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4c1d95 100%)',
                      border: '1px solid rgba(167,139,250,0.3)',
                    }}
                  >
                    {/* Top glow accent */}
                    <div
                      className="h-1 w-full"
                      style={{ background: 'linear-gradient(90deg, #818cf8, #a78bfa, #c084fc)' }}
                    />

                    <div className="p-8 text-center space-y-6">
                      {/* Icon */}
                      <div className="flex justify-center">
                        <div
                          className="w-16 h-16 rounded-2xl flex items-center justify-center"
                          style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.3)' }}
                        >
                          <Zap className="h-8 w-8 text-violet-300" />
                        </div>
                      </div>

                      {/* Headline */}
                      <div className="space-y-2">
                        <h2 className="text-xl font-bold text-white tracking-tight">
                          No Active Plan
                        </h2>
                        <p className="text-sm text-violet-200 leading-relaxed">
                          This section requires an active subscription.<br />
                          Subscribe to a plan or ask your admin to assign one.
                        </p>
                      </div>

                      {/* Feature pills */}
                      <div className="flex flex-wrap justify-center gap-2">
                        {['SEO Execution', 'Keyword Tracking', 'Backlinks', 'AI Recommendations', 'Reports'].map(f => (
                          <span
                            key={f}
                            className="text-xs px-2.5 py-1 rounded-full font-medium"
                            style={{ background: 'rgba(167,139,250,0.15)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.2)' }}
                          >
                            {f}
                          </span>
                        ))}
                      </div>

                      {/* CTA buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 pt-1">
                        <button
                          onClick={() => navigate('/dashboard/billing')}
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-95"
                          style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
                        >
                          <Package className="h-4 w-4" />
                          View Plans &amp; Subscribe
                        </button>
                        <a
                          href="mailto:support@godigitalstep.com"
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all hover:bg-white/10 active:scale-95"
                          style={{ border: '1px solid rgba(167,139,250,0.4)', color: '#ddd6fe' }}
                        >
                          <Mail className="h-4 w-4" />
                          Contact Admin
                        </a>
                      </div>

                      {/* Dashboard link */}
                      <p className="text-xs text-violet-400">
                        You can still access your{' '}
                        <button
                          onClick={() => navigate('/dashboard')}
                          className="underline underline-offset-2 text-violet-300 hover:text-white transition-colors"
                        >
                          main dashboard
                        </button>
                        {' '}while your plan is inactive.
                      </p>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            ) : (
              <Outlet />
            );
          })()}
        </main>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <ProjectProvider>
      <PackageGatesProvider>
        <DashboardLayoutInner />
      </PackageGatesProvider>
    </ProjectProvider>
  );
}
